import { plantDetailsBreadcrumb } from "../plants/plants-breadcrumbs";

/**
 * Interface defining the parameters for component breadcrumb functions.
 * 
 * @interface IParams
 * @property {Object} client - Client information
 * @property {string} [client.name] - Name of the client
 * @property {number} client.id - ID of the client
 * @property {Object} building - Building information
 * @property {string} [building.name] - Name of the building
 * @property {number} building.id - ID of the building
 * @property {Object} [plant] - Plant information (optional)
 * @property {string} [plant.name] - Name of the plant
 * @property {number} [plant.id] - ID of the plant
 * @property {Object} [component] - Component information (optional)
 * @property {string} [component.name] - Name of the component
 * @property {number} [component.id] - ID of the component
 * @property {Object} [history] - History information (optional)
 * @property {string} [history.name] - Name of the history entry
 * @property {string} [history.id] - ID of the history entry
 */
export interface IParams {
  client: { name?: string; id: number };
  building: { name?: string; id: number };
  plant?: { name?: string; id: number };
  component?: { name?: string; id: number };
  history?: { name?: string; id: string }
}

/**
 * Generates breadcrumb navigation for the components list view.
 * Creates a hierarchical path showing the client, building, plant, and components context.
 * 
 * @function componentsListBreadcrumbs
 * @param {IParams} props - Parameters containing client, building, and plant information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 * 
 * @example
 * ```tsx
 * const breadcrumbs = componentsListBreadcrumbs({
 *   client: { name: "Client Name", id: 1 },
 *   building: { name: "Building Name", id: 2 },
 *   plant: { name: "Plant Name", id: 3 }
 * });
 * ```
 */
export const componentsListBreadcrumbs = (props: IParams) => {
  const { client, building, plant } = props;

  const plantsDetails = plantDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
    plant: { name: plant?.name, id: Number(plant?.id) },
  });

  const componentsList = [
    ...plantsDetails,
    {
      title: "Komponenten",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
      disabledPath: true,
    },
  ];

  return componentsList.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for the reports details view.
 * Creates a path showing the client, building, plant, and history context.
 * 
 * @function reportsDetailsBreadcrumbs
 * @param {IParams} props - Parameters containing client, building, plant, and history information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 */
export const reportsDetailsBreadcrumbs = (props: IParams) => {
  const { client, building, plant, history } = props;


  const plantsDetails = plantDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
    plant: { name: plant?.name, id: Number(plant?.id) },
  });

  const componentDetails = [
    ...plantsDetails,
    {
      title: history?.name,
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/history/${history?.id}`,
      disabledPath: true,
    },
  ];

  return componentDetails.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for the component details view.
 * Creates a path showing the client, building, plant, and specific component context.
 * 
 * @function componentDetailsBreadcrumbs
 * @param {IParams} props - Parameters containing client, building, plant, and component information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 */
export const componentDetailsBreadcrumbs = (props: IParams) => {
  const { client, building, plant, component } = props;


  const plantsDetails = plantDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
    plant: { name: plant?.name, id: Number(plant?.id) },
  });

  const componentDetails = [
    ...plantsDetails,
    {
      title: "Komponenten",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
    },
    {
      title: component?.name,
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
      disabledPath: true,
    },
  ];

  return componentDetails.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for the component creation view.
 * Creates a path showing the client, building, plant context and indicating new component creation.
 * 
 * @function createComponentsBreadcrumbs
 * @param {IParams} props - Parameters containing client, building, and plant information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 */
export const createComponentsBreadcrumbs = (props: IParams) => {
  const { client, building, plant } = props;

  const plantsDetails = plantDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
    plant: { name: plant?.name, id: Number(plant?.id) },
  });

  const componentsList = [
    ...plantsDetails,
    {
      title: "Komponenten",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
    },
    {
      title: "Erstellen",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
      disabledPath: true,
    },
  ];

  return componentsList.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for the component update view.
 * Creates a path showing the client, building, plant, and specific component context for updating.
 * 
 * @function updateComponentsBreadcrumbs
 * @param {IParams} props - Parameters containing client, building, plant, and component information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 */
export const updateComponentsBreadcrumbs = (props: IParams) => {
  const { client, building, plant, component } = props;
  const plantsDetails = plantDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
    plant: { name: plant?.name, id: Number(plant?.id) },
  });

  const componentsList = [
    ...plantsDetails,
    {
      title: "Komponenten",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components`,
    },
    {
      title: component?.name,
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components/${component?.id}`,
      disabledPath: true,
    },
    {
      title: "Aktualisieren",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}/components/${component?.id}`,
      disabledPath: true,
    },
  ];

  return componentsList.filter((item) => item.title !== "");
};
